'use client';

import React, { use } from 'react';
import LaporanHeader from '@/components/yayasan/keuangan/LaporanHeader';
import { SchoolUnit, validUnits, getUnitFinancialStats, getUnitJournal, getUnitChartData } from '@/lib/data';
import { notFound } from 'next/navigation';
import {
    TrendingUp,
    TrendingDown,
    Wallet,
    RefreshCw,
    PieChart,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';

import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-surface border border-border-main p-4 rounded-2xl shadow-xl">
                <p className="font-bold text-foreground mb-2 text-sm">{label}</p>
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary"></div>
                        <span className="text-xs text-secondary font-medium uppercase tracking-widest">Pemasukan:</span>
                        <span className="text-xs font-black text-foreground ml-auto tabular-nums">
                            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(payload[0].value)}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-error"></div>
                        <span className="text-xs text-secondary font-medium uppercase tracking-widest">Pengeluaran:</span>
                        <span className="text-xs font-black text-foreground ml-auto tabular-nums">
                            {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(payload[1].value)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }
    return null;
};

export default function PelaporanUnitPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit: initialUnit } = use(params);
    const [activeUnit, setActiveUnit] = React.useState<SchoolUnit>(initialUnit as SchoolUnit);

    if (!validUnits.includes(initialUnit as SchoolUnit)) {
        notFound();
    }

    const stats = getUnitFinancialStats(activeUnit);
    const journal = getUnitJournal(activeUnit);
    const chartData = getUnitChartData(activeUnit);

    const SUMMARY_CARDS = [
        {
            title: 'Total Pemasukan',
            value: stats.income,
            trend: '+12.5%',
            isPositive: true,
            icon: TrendingUp,
            color: 'text-success',
            bg: 'bg-success-soft'
        },
        {
            title: 'Total Pengeluaran',
            value: stats.expense,
            trend: '+5.2%',
            isPositive: false,
            icon: TrendingDown,
            color: 'text-error',
            bg: 'bg-error-soft'
        },
        {
            title: 'Saldo Akhir',
            value: stats.balance,
            trend: '+20.1%',
            isPositive: true,
            icon: Wallet,
            color: 'text-primary',
            bg: 'bg-primary-soft'
        },
        {
            title: 'Jumlah Transaksi',
            value: `${journal.length} Transaksi`,
            trend: 'Bulan ini',
            isPositive: true,
            icon: RefreshCw,
            color: 'text-secondary',
            bg: 'bg-secondary-soft'
        },
    ];

    const DATA_CATEGORIES = [
        { category: 'Pemasukan SPP Siswa', total: stats.income, percentage: 71 },
        { category: 'Pemasukan Dana BOS', total: 'Rp 0', percentage: 19 },
        { category: 'Pemasukan Lain-lain', total: 'Rp 0', percentage: 10 },
        { category: 'Gaji Guru & Staff', total: stats.expense, percentage: 58 },
        { category: 'Operasional Kantor', total: 'Rp 0', percentage: 15 },
        { category: 'Pemeliharaan Gedung', total: 'Rp 0', percentage: 27 },
    ];

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24 animate-in fade-in duration-700">
            <LaporanHeader
                title="Pelaporan Unit"
                description="Ringkasan kondisi dan performa keuangan unit secara komprehensif"
                activeUnit={activeUnit}
                onUnitChange={setActiveUnit}
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {SUMMARY_CARDS.map((card, i) => (
                    <div key={i} className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm hover:shadow-md transition-all group overflow-hidden relative">
                        <div className="flex flex-col gap-4 relative z-10">
                            <div className={`w-12 h-12 ${card.bg} ${card.color} rounded-2xl flex items-center justify-center shadow-sm`}>
                                <card.icon className="w-6 h-6" />
                            </div>
                            <div>
                                <p className="text-[10px] font-bold text-muted uppercase tracking-widest ml-0.5">{card.title}</p>
                                <h3 className="text-2xl font-bold text-foreground mt-1">{card.value}</h3>
                            </div>
                            <div className="flex items-center gap-1.5 pt-1">
                                <span className={`flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full ${card.isPositive ? 'bg-success-soft text-success' : 'bg-error-soft text-error'}`}>
                                    {card.isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                                    {card.trend}
                                </span>
                                <span className="text-[10px] font-bold text-muted uppercase tracking-wider">vs bln lalu</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Performance Chart / Box */}
                <div className="lg:col-span-2 bg-surface p-8 rounded-4xl border border-border-main shadow-sm space-y-8">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <h3 className="text-xl font-bold flex items-center gap-3">
                                <TrendingUp className="w-5 h-5 text-primary" /> Tren Keuangan Bulanan
                            </h3>
                            <p className="text-xs text-secondary font-medium italic select-none">Perbandingan pemasukan dan pengeluaran 1 tahun terakhir</p>
                        </div>
                    </div>

                    {/* Chart Area */}
                    <div className="h-[350px] w-full pt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{
                                    top: 10,
                                    right: 10,
                                    left: 0,
                                    bottom: 0,
                                }}
                                barGap={4}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2ECE6" opacity={0.5} />
                                <XAxis
                                    dataKey="month"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#8da495', fontSize: 10, fontWeight: 700 }}
                                    dy={10}
                                />
                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={{ fill: 'var(--color-primary-soft)', opacity: 0.3, radius: 8 }}
                                />
                                <Bar
                                    dataKey="income"
                                    fill="var(--color-primary)"
                                    radius={[4, 4, 0, 0]}
                                    barSize={12}
                                />
                                <Bar
                                    dataKey="expense"
                                    fill="var(--color-error)"
                                    radius={[4, 4, 0, 0]}
                                    barSize={12}
                                    fillOpacity={0.3}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="flex items-center gap-6 pt-4 border-t border-border-main/50">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-primary" />
                            <span className="text-[10px] font-bold text-secondary uppercase tracking-widest">Total Pemasukan</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-error/30 border border-error/50" />
                            <span className="text-[10px] font-bold text-secondary uppercase tracking-widest">Total Pengeluaran</span>
                        </div>
                    </div>
                </div>

                {/* Categories Summary */}
                <div className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm space-y-8">
                    <div className="space-y-1">
                        <h3 className="text-xl font-bold flex items-center gap-3">
                            <PieChart className="w-5 h-5 text-primary" /> Alokasi Dana
                        </h3>
                        <p className="text-xs text-secondary font-medium">Ringkasan transaksi berdasarkan kategori</p>
                    </div>

                    <div className="space-y-6">
                        {DATA_CATEGORIES.map((item, i) => (
                            <div key={i} className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <span className="text-sm font-bold text-foreground">{item.category}</span>
                                    <span className="text-xs font-bold text-primary">{item.total}</span>
                                </div>
                                <div className="h-2 w-full bg-background rounded-full overflow-hidden">
                                    <div
                                        className={`h-full rounded-full ${i < 3 ? 'bg-primary' : 'bg-error'} opacity-80`}
                                        style={{ width: `${item.percentage}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <button className="w-full py-4 border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest text-secondary hover:bg-primary-soft/30 hover:text-primary hover:border-primary/30 transition-all">
                        Lihat Laporan Detail
                    </button>
                </div>
            </div>
        </main>
    );
}
